import { useState, useEffect, useRef } from 'react';
import { Check, X, Minus, ListChecks, AlertCircle, AlertTriangle, Camera, RefreshCw } from 'lucide-react';
import { vyhodnotMereni, parseCislo, fmt } from './mereniKontrola';
import { MereniNapoveda } from './MereniPole';
import ZnamenkoTlacitko, { muzeZaporne } from '../../components/ui/ZnamenkoTlacitko';

const KONTROLA_MOZNOSTI = ['OK', 'NOK', 'N/A'];
const KONTROLA_BARVY = { OK: '#10B981', NOK: '#EF4444', 'N/A': '#6B7280' };

/** Vyparsuje moznosti_json (typ='vyber'). Default OK/NOK/N/A. */
function parseMoznosti(json) {
    try {
        let m = JSON.parse(json || '[]');
        if (!Array.isArray(m)) m = [];
        m = m.map(x => typeof x === 'string' ? { text: x } : x);
        if (m.length === 0) return [{ text: 'OK' }, { text: 'NOK', negativni: true }, { text: 'N/A' }];
        return m;
    } catch { return [{ text: 'OK' }, { text: 'NOK', negativni: true }, { text: 'N/A' }]; }
}

/**
 * Interaktivní panel checklistů — všechny typy položek řešené INLINE,
 * žádné modaly. Podporované typy:
 *   checkbox  — toggle button (klik na řádek)
 *   kontrola  — OK/NOK/N/A grid (NOK → povinná poznámka akce)
 *   cislo     — number input + jednotka + min/max range (mimo limit → povinná poznámka)
 *   vyber     — pills (negativní možnost → povinná poznámka)
 *   text      — debounced textarea
 *   foto      — file picker (camera capture na mobilu) + thumbnail preview
 *
 * Data shape jako v sestavFazi() — `faze.checklisty[].items[]`.
 */
export default function ChecklistPanel({ faze, onSave, isDark, fill = false }) {
    if (!faze || !Array.isArray(faze.checklisty) || faze.checklisty.length === 0) return null;

    const slotBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
    const slotBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';

    const totalDone = faze.checklisty.reduce((a, c) => a + (c.done_count || 0), 0);
    const totalCelkem = faze.checklisty.reduce((a, c) => a + (c.total || 0), 0);
    const allDone = totalDone === totalCelkem;
    // Sjednocení s barvou fáze v cockpitu — stejný tint na icon boxu jako má
    // ShiftCard* sekce (Pre modrá / Starting oranžová / Active zelená / End fialová).
    const phaseColor =
        faze.id === 'zahajeni' ? '#FF8A00' :
        faze.id === 'ukonceni' ? '#975ACD' :
        faze.id === 'prubeh'   ? '#10B981' :
        /* fallback */           '#0CC0DF';
    // Count chip — zelená když hotovo, jinak v barvě fáze.
    const countColor = allDone ? '#10B981' : phaseColor;
    const sectionTitle =
        faze.id === 'ukonceni' ? 'Ukončovací kontroly' :
        faze.id === 'prubeh'   ? 'Průběžné kontroly'   :
        'Úvodní kontroly';

    return (
        <div style={{
            marginTop: 6,
            padding: '0 12px',
            display: 'flex', flexDirection: 'column',
            ...(fill ? { flex: 1, minHeight: 0 } : null),
        }}>
            {/* Header — sjednoceno s CockpitInfoCol designem: tinted icon box
                vlevo + uppercase label + count pill v barvě fáze (zelená když
                všechno splněno). */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                flexShrink: 0, padding: '4px 2px 10px',
                position: 'relative', zIndex: 2,
            }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{
                        width: 26, height: 26, borderRadius: 5, flexShrink: 0,
                        background: `${phaseColor}1A`, color: phaseColor,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <ListChecks size={14} />
                    </span>
                    <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                        textTransform: 'uppercase', color: labelMuted,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{sectionTitle}</span>
                </span>
                <span style={{
                    fontSize: 11, fontWeight: 800,
                    padding: '3px 9px', borderRadius: 999,
                    background: `${countColor}1A`, color: countColor,
                    border: `1px solid ${countColor}55`,
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: 0.3,
                    flexShrink: 0,
                }}>{totalDone}/{totalCelkem}</span>
            </div>

            {/* Scroll oblast — vlastní scroll, nezávislý na stránce.
                Paternoster efekt: mask-image bledne nahoře i dole — položky
                u hran zóny vidění mizí do průhledné, jako by zajížděly do slotu. */}
            <div style={{
                display: 'flex', flexDirection: 'column', gap: 6,
                ...(fill ? {
                    flex: 1, minHeight: 0, overflowY: 'auto',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 28px, #000 calc(100% - 28px), transparent 100%)',
                    maskImage: 'linear-gradient(to bottom, transparent 0, #000 28px, #000 calc(100% - 28px), transparent 100%)',
                } : null),
                margin: fill ? '0 -4px' : 0,
                // Spodní výplň: v cockpitu na mobilu panel končí u hrany
                // displeje, poslední položka musí dojet nad home indicator.
                padding: fill ? '12px 4px calc(12px + env(safe-area-inset-bottom, 0px))' : 0,
            }}>
                {faze.checklisty.flatMap(ch =>
                    (ch.items || []).map(item => (
                        <ChecklistItem
                            key={`${ch.id ?? ch.sablona_id}-${item.id}`}
                            item={item}
                            checklistId={ch.id}
                            preview={ch.preview}
                            onSave={onSave}
                            slotBg={slotBg} slotBorder={slotBorder}
                            textPrimary={textPrimary} textMuted={textMuted} labelMuted={labelMuted}
                            isDark={isDark}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function ChecklistItem({ item, checklistId, preview, onSave, slotBg, slotBorder, textPrimary, textMuted, labelMuted, isDark }) {
    const typ = item.typ_odpovedi || 'checkbox';
    const odp = item.odpoved || {};
    const splneno = !!item.splneno;
    const mimoLimit = !!odp.mimo_limit;

    // Číslo (měření): hodnota, jak ji člověk právě píše — pod řádkem se podle
    // ní hned ukazuje, jestli sedí (limit, přírůstek, překlep). Uloží se až po
    // dopsání (opuštění pole / Enter), ne při každém úhozu.
    const [cisloVal, setCisloVal] = useState(odp.hodnota_cislo != null ? String(odp.hodnota_cislo).replace('.', ',') : '');
    const cisloFokus = useRef(false);
    useEffect(() => {
        if (cisloFokus.current) return;
        setCisloVal(odp.hodnota_cislo != null ? fmt(Number(odp.hodnota_cislo)).replace(/\s/g, '') : '');
    }, [odp.hodnota_cislo]);
    const ulozCislo = (raw) => {
        const v = parseCislo(raw);
        if (Number.isNaN(v)) return;
        const puvodni = odp.hodnota_cislo != null ? Number(odp.hodnota_cislo) : null;
        if (v === puvodni) return;
        onSave?.(checklistId, item.id, { hodnota_cislo: v });
    };

    const isCheckbox = typ === 'checkbox';
    const isKontrola = typ === 'kontrola';
    const isCislo = typ === 'cislo';
    const isVyber = typ === 'vyber';
    const isText = typ === 'text';
    const isFoto = typ === 'foto';

    const moznosti = isVyber ? parseMoznosti(item.moznosti_json) : [];
    const vybranaMoznost = isVyber ? moznosti.find(m => m.text === odp.hodnota_vyber) : null;

    const jeNok =
        (isKontrola && odp.hodnota_vyber === 'NOK') ||
        (isVyber && !!vybranaMoznost?.negativni) ||
        (isCislo && mimoLimit);
    const jeNa = isKontrola && odp.hodnota_vyber === 'N/A';

    // Status ikona vlevo
    let leftIcon = null;
    let leftBg = 'rgba(0,0,0,0.30)';
    let leftBorder = 'rgba(255,255,255,0.15)';
    let leftColor = 'transparent';
    if (jeNok) {
        leftBg = 'rgba(239,68,68,0.20)';
        leftBorder = 'rgba(239,68,68,0.55)';
        leftColor = '#EF4444';
        leftIcon = isKontrola ? <X size={14} strokeWidth={3} /> : <AlertCircle size={14} />;
    } else if (jeNa) {
        leftBg = 'rgba(107,114,128,0.20)';
        leftBorder = 'rgba(107,114,128,0.55)';
        leftColor = '#9CA3AF';
        leftIcon = <Minus size={14} strokeWidth={3} />;
    } else if (splneno) {
        leftBg = 'rgba(16,185,129,0.20)';
        leftBorder = 'rgba(16,185,129,0.55)';
        leftColor = '#10B981';
        leftIcon = <Check size={16} strokeWidth={3} />;
    }

    const handleCheckboxToggle = () => {
        if (preview) return;
        onSave?.(checklistId, item.id, { hodnota_bool: !splneno });
    };
    const handleSegment = (e, m) => {
        e.stopPropagation();
        if (preview) return;
        const nova = odp.hodnota_vyber === m ? null : m;
        onSave?.(checklistId, item.id, { hodnota_vyber: nova });
    };
    const handleVyber = (m) => {
        if (preview) return;
        const nova = odp.hodnota_vyber === m.text ? null : m.text;
        onSave?.(checklistId, item.id, { hodnota_vyber: nova });
    };

    const rowClickable = !preview && isCheckbox;
    const lineThrough = splneno && (isCheckbox || (isKontrola && odp.hodnota_vyber === 'OK'));
    const textColor = lineThrough ? textMuted : textPrimary;

    // Hint pod text (jen u nesplněných položek nestandardních typů)
    const hint = !splneno
        ? (isCislo ? (cisloVal.trim() ? null : 'Zadej hodnotu') : isVyber ? 'Vyber možnost' : isText ? 'Napiš poznámku' : isFoto ? 'Vyfoť' : null)
        : null;

    // ─── KONTROLA (OK/NOK/N/A) — odlišný layout: text v plné šíři nahoře,
    //     pod ním 3 tlačítka v 1 řádku full-width. Bez levého checkboxu.
    //     Stav položky se odráží v barevném okraji + jemném tintu. ────────────
    if (isKontrola) {
        const sel = odp.hodnota_vyber;
        const selColor = sel ? KONTROLA_BARVY[sel] : null;
        const itemBorder = selColor
            ? `${selColor}66`
            : slotBorder;
        const itemBg = selColor
            ? `${selColor}0F`
            : slotBg;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{
                    display: 'flex', flexDirection: 'column', gap: 8,
                    padding: 10, borderRadius: 7,
                    background: itemBg, border: `1px solid ${itemBorder}`,
                    borderLeftWidth: selColor ? 3 : 1,
                    borderLeftColor: selColor || slotBorder,
                }}>
                    <span style={{
                        fontSize: 13, fontWeight: 400, lineHeight: 1.3,
                        color: textColor,
                        textDecoration: lineThrough ? 'line-through' : 'none',
                    }}>
                        {item.text}
                        {item.povinne && !splneno && (
                            <span style={{ color: '#FF8A00', marginLeft: 4 }}>•</span>
                        )}
                    </span>

                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
                    }}>
                        {KONTROLA_MOZNOSTI.map(m => {
                            const isSel = sel === m;
                            const col = KONTROLA_BARVY[m];
                            return (
                                <button key={m} type="button" disabled={preview}
                                    onClick={(e) => handleSegment(e, m)}
                                    style={{
                                        fontSize: 12, padding: '8px 6px', borderRadius: 6, fontWeight: 800,
                                        background: isSel ? `${col}22` : 'transparent',
                                        border: `1px solid ${isSel ? col : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                                        color: isSel ? col : labelMuted,
                                        cursor: preview ? 'default' : 'pointer',
                                        fontVariantNumeric: 'tabular-nums',
                                        letterSpacing: 0.4,
                                    }}>{m}</button>
                            );
                        })}
                    </div>
                </div>

                {jeNok && !preview && (
                    <NokAkcePanel
                        item={item}
                        odp={odp}
                        typ={typ}
                        checklistId={checklistId}
                        onSave={onSave}
                        isDark={isDark}
                        noLeftIndent
                    />
                )}
            </div>
        );
    }

    // Stavová barva levého okraje + tint pozadí — sjednoceno s kontrola
    // layoutem: splneno=green, NOK/mimo limit=red. Bez stavu neutralni.
    const stateColor = jeNok ? '#EF4444' : (splneno ? '#10B981' : null);
    const rowBorder = stateColor ? `${stateColor}66` : slotBorder;
    const rowBg = stateColor ? `${stateColor}0F` : slotBg;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Hlavní řádek */}
            <div
                onClick={rowClickable ? handleCheckboxToggle : undefined}
                style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: 10, borderRadius: 7,
                    background: rowBg, border: `1px solid ${rowBorder}`,
                    borderLeftWidth: stateColor ? 3 : 1,
                    borderLeftColor: stateColor || slotBorder,
                    cursor: rowClickable ? 'pointer' : 'default',
                    color: textPrimary,
                }}>
                <span style={{
                    width: 26, height: 26, borderRadius: 5, flexShrink: 0,
                    background: leftBg, border: `1.5px solid ${leftBorder}`,
                    color: leftColor,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {leftIcon}
                </span>

                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{
                        fontSize: 13, fontWeight: 400, lineHeight: 1.3,
                        color: textColor,
                        textDecoration: lineThrough ? 'line-through' : 'none',
                    }}>
                        {item.text}
                        {item.povinne && !splneno && (
                            <span style={{ color: '#FF8A00', marginLeft: 4 }}>•</span>
                        )}
                    </span>
                    {/* Bod už dnes / tento týden změřil někdo jiný — položka se převzala */}
                    {odp.prevzato && (
                        <span style={{ fontSize: 11, color: labelMuted }}>
                            Změřil(a) {odp.prevzato.kdo || 'kolega'}{odp.prevzato.kdy ? ` · ${new Date(odp.prevzato.kdy).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                        </span>
                    )}
                    {hint && (
                        <span style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                            textTransform: 'uppercase', color: labelMuted,
                        }}>{hint}</span>
                    )}
                </div>

                {isCislo && (
                    <CisloInput
                        item={item}
                        val={cisloVal}
                        setVal={setCisloVal}
                        fokusRef={cisloFokus}
                        onUloz={ulozCislo}
                        preview={preview}
                        labelMuted={labelMuted}
                        textPrimary={textPrimary}
                        stav={vyhodnotMereni(cisloVal, item, checklistId)?.stav}
                        mimoLimit={mimoLimit}
                        isDark={isDark}
                    />
                )}

                {isFoto && !odp.foto_cesta && (
                    <FotoButton
                        checklistId={checklistId}
                        polozkaId={item.id}
                        disabled={preview}
                        onUploaded={() => onSave?.(checklistId, item.id, { hodnota_bool: true })}
                        isDark={isDark}
                    />
                )}
            </div>

            {/* Měření: okamžitá nápověda pod řádkem (minule, limit, přírůstek, překlep) */}
            {isCislo && !preview && (
                <MereniNapoveda vysledek={vyhodnotMereni(cisloVal, item, checklistId)} isDark={isDark} labelMuted={labelMuted}
                    onOpravit={(n) => { setCisloVal(fmt(n).replace(/\s/g, '')); ulozCislo(String(n)); }} />
            )}

            {/* Pills pro vyber — pod hlavním řádkem */}
            {isVyber && (
                <div style={{
                    marginLeft: 36, display: 'flex', gap: 4, flexWrap: 'wrap',
                }}>
                    {moznosti.map(m => {
                        const sel = odp.hodnota_vyber === m.text;
                        const col = m.negativni ? '#EF4444' : '#B47AEA';
                        return (
                            <button key={m.text} type="button" disabled={preview}
                                onClick={() => handleVyber(m)}
                                style={{
                                    fontSize: 12, padding: '5px 11px', borderRadius: 5, fontWeight: sel ? 700 : 600,
                                    background: sel ? `${col}22` : 'transparent',
                                    border: `1px solid ${sel ? col : 'rgba(255,255,255,0.10)'}`,
                                    color: sel ? col : labelMuted,
                                    cursor: preview ? 'default' : 'pointer',
                                }}>{m.text}</button>
                        );
                    })}
                </div>
            )}

            {/* Textarea pro text — pod hlavním řádkem */}
            {isText && (
                <div style={{ marginLeft: 36 }}>
                    <DebouncedTextarea
                        initial={odp.hodnota_text || ''}
                        onSave={(val) => onSave?.(checklistId, item.id, { hodnota_text: val })}
                        placeholder="Napiš poznámku…"
                        disabled={preview}
                        isDark={isDark}
                    />
                </div>
            )}

            {/* Foto preview po nahrání — pod hlavním řádkem */}
            {isFoto && odp.foto_cesta && (
                <div style={{ marginLeft: 36 }}>
                    <FotoPreview
                        fotoCesta={odp.foto_cesta}
                        checklistId={checklistId}
                        polozkaId={item.id}
                        disabled={preview}
                        onChanged={() => onSave?.(checklistId, item.id, { hodnota_bool: true })}
                        isDark={isDark}
                    />
                </div>
            )}

            {/* NOK panel — kontrola NOK, vyber negativní, cislo mimo limit */}
            {jeNok && !preview && (
                <NokAkcePanel
                    item={item}
                    odp={odp}
                    typ={typ}
                    checklistId={checklistId}
                    onSave={onSave}
                    isDark={isDark}
                />
            )}
        </div>
    );
}

function CisloInput({ item, val, setVal, fokusRef, onUloz, preview, labelMuted, textPrimary, stav, mimoLimit, isDark }) {
    const jednotka = item.mereni_kontext?.jednotka || item.jednotka || item.parametr?.jednotka || '';
    // Barva pole podle okamžité kontroly; po uložení rozhoduje server (mimoLimit)
    const barva = stav === 'chyba' ? '#EF4444' : stav === 'pozor' ? '#F59E0B' : stav === 'ok' ? '#10B981' : null;
    const cervene = mimoLimit && !stav;
    return (
        <div onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {muzeZaporne(item) && (
                <ZnamenkoTlacitko hodnota={val} disabled={preview} isDark={isDark} vyska={34}
                    onZmena={n => { setVal(n); if (!fokusRef.current) onUloz(n); }} />
            )}
            <input type="text" inputMode="decimal" autoComplete="off"
                value={val}
                disabled={preview}
                onFocus={() => { fokusRef.current = true; }}
                onChange={e => setVal(e.target.value)}
                onBlur={e => { fokusRef.current = false; onUloz(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                placeholder="—"
                style={{
                    width: 92, padding: '7px 9px', borderRadius: 6, fontSize: 15, fontWeight: 700,
                    background: isDark ? 'rgba(0,0,0,0.30)' : 'var(--surface-base)',
                    color: barva || (cervene ? '#EF4444' : textPrimary),
                    border: `1.5px solid ${barva || (cervene ? '#EF4444' : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'))}`,
                    fontVariantNumeric: 'tabular-nums', textAlign: 'right', outline: 'none',
                }}
            />
            {jednotka && (
                <span style={{ fontSize: 11, fontWeight: 600, color: labelMuted, minWidth: 18 }}>{jednotka}</span>
            )}
        </div>
    );
}

function DebouncedTextarea({ initial, onSave, placeholder, disabled, isDark }) {
    const [text, setText] = useState(initial);
    const lastSaved = useRef(initial);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) return;
        if (initial !== text && initial !== lastSaved.current) {
            setText(initial);
            lastSaved.current = initial;
        }
    }, [initial]); // eslint-disable-line

    const handleChange = (val) => {
        setText(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            lastSaved.current = val;
            debounceRef.current = null;
            onSave?.(val);
        }, 600);
    };
    const handleBlur = () => {
        if (!debounceRef.current) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        if (text !== lastSaved.current) {
            lastSaved.current = text;
            onSave?.(text);
        }
    };

    return (
        <textarea
            value={text}
            disabled={disabled}
            onChange={e => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={placeholder}
            spellCheck={false}
            className={isDark ? 'ms-textarea-dark' : 'ms-textarea-light'}
            style={{
                width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                resize: 'vertical', minHeight: 56, boxSizing: 'border-box',
                fontFamily: 'inherit',
                colorScheme: isDark ? 'dark' : 'light',
                outline: 'none', appearance: 'none',
                WebkitAppearance: 'none', MozAppearance: 'none',
            }}
        />
    );
}

function FotoButton({ checklistId, polozkaId, disabled, onUploaded, isDark }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('foto', file);
        fd.append('polozka_id', polozkaId);
        try {
            const res = await fetch(`/api/checklisty/${checklistId}/foto`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': csrf(), Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: fd,
            });
            if (res.ok) onUploaded?.();
            else {
                const data = await res.json().catch(() => ({}));
                alert(data?.error || data?.message || `Nahrání selhalo (HTTP ${res.status})`);
            }
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <>
            <button type="button" disabled={disabled || uploading}
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 10px', borderRadius: 5, flexShrink: 0,
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-default)'}`,
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                    color: isDark ? 'rgba(255,255,255,0.85)' : 'var(--text-primary)',
                    cursor: (disabled || uploading) ? 'not-allowed' : 'pointer',
                    fontSize: 11, fontWeight: 600,
                }}>
                <Camera size={13} /> {uploading ? 'Nahrávám…' : 'Vyfotit'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={e => handleUpload(e.target.files?.[0])} />
        </>
    );
}

function FotoPreview({ fotoCesta, checklistId, polozkaId, disabled, onChanged, isDark }) {
    const fileRef = useRef(null);
    const [url, setUrl] = useState(`/storage/${fotoCesta}`);
    const [uploading, setUploading] = useState(false);
    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleReplace = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('foto', file);
        fd.append('polozka_id', polozkaId);
        try {
            const res = await fetch(`/api/checklisty/${checklistId}/foto`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': csrf(), Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: fd,
            });
            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                if (data.foto_url) setUrl(data.foto_url);
                onChanged?.();
            } else {
                alert(`Nahrání selhalo (HTTP ${res.status})`);
            }
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={url} alt="Foto"
                style={{
                    maxWidth: 200, maxHeight: 140, borderRadius: 6, objectFit: 'cover', display: 'block',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                }} />
            {!disabled && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    style={{
                        position: 'absolute', bottom: 6, right: 6,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 10, padding: '4px 8px', borderRadius: 4,
                        background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none',
                        cursor: uploading ? 'wait' : 'pointer',
                    }}>
                    <RefreshCw size={11} /> {uploading ? '…' : 'Změnit'}
                </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={e => handleReplace(e.target.files?.[0])} />
        </div>
    );
}

/** Panel s povinnou poznámkou akce — fire na NOK kontrola, negativní vyber, cislo mimo limit. */
function NokAkcePanel({ item, odp, typ, checklistId, onSave, isDark, noLeftIndent = false }) {
    const initial = odp.poznamka_akce || '';
    const [text, setText] = useState(initial);
    const lastSaved = useRef(initial);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) return;
        if (initial !== text && initial !== lastSaved.current) setText(initial);
    }, [initial]); // eslint-disable-line

    const handleChange = (val) => {
        setText(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            lastSaved.current = val;
            debounceRef.current = null;
            onSave?.(checklistId, item.id, { poznamka_akce: val });
        }, 600);
    };
    const handleBlur = () => {
        if (!debounceRef.current) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        if (text !== lastSaved.current) {
            lastSaved.current = text;
            onSave?.(checklistId, item.id, { poznamka_akce: text });
        }
    };

    const limit = item.parametr?.aktualni_limit;
    const instr = (typ === 'cislo'
        ? (limit?.instrukce_min || limit?.instrukce_max || item.instrukce_mereni)
        : item.instrukce_mereni) || '';
    const podezrele = typ === 'cislo' && odp.mereni?.podezrele;
    const navrh = typ === 'cislo' ? odp.mereni?.navrh : null;
    const title = typ === 'kontrola' ? 'Kontrola nesplněna'
        : typ === 'vyber' ? `Negativní odpověď: ${odp.hodnota_vyber}`
        : podezrele && odp.mereni?.stav !== 'mimo' ? 'Tohle vypadá jako překlep'
        : 'Hodnota mimo limit';
    const prazdne = !text.trim();

    return (
        <div style={{
            marginLeft: noLeftIndent ? 0 : 36, padding: '10px 12px', borderRadius: 7,
            background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.04)',
            border: '1px solid rgba(239,68,68,0.20)',
        }}>
            <div style={{
                fontSize: 11, fontWeight: 700, color: '#EF4444',
                marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6,
                letterSpacing: 0.3, textTransform: 'uppercase',
            }}>
                <AlertTriangle size={13} /> {title}
            </div>
            {podezrele && (
                <p style={{ fontSize: 12.5, margin: '0 0 8px', fontWeight: 500, color: isDark ? '#fff' : 'var(--text-primary)', lineHeight: 1.4 }}>
                    Hodnota nesedí s minulými odečty{navrh != null ? ' — nechybí desetinná čárka?' : '. Zkontroluj ji na displeji měřidla.'}
                    {navrh != null && (
                        <button type="button" onClick={() => onSave?.(checklistId, item.id, { hodnota_cislo: navrh })}
                            style={{ display: 'block', marginTop: 8, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                background: '#06B6D4', color: '#06282E', fontWeight: 700, fontSize: 13 }}>
                            Opravit na {Number(navrh).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })}
                        </button>
                    )}
                </p>
            )}
            {instr && !podezrele && (
                <p style={{ fontSize: 12, margin: '0 0 8px', fontWeight: 500,
                    color: isDark ? '#fff' : 'var(--text-primary)', lineHeight: 1.4 }}>{instr}</p>
            )}
            <label style={{
                fontSize: 10, fontWeight: 700, color: '#EF4444',
                display: 'block', marginBottom: 4, letterSpacing: 0.3,
            }}>
                Co se stalo / provedená akce {prazdne && <span style={{ color: '#FF8A00' }}>•</span>}
            </label>
            <textarea
                value={text}
                onChange={e => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder="Popiš situaci a jakou akci jsi provedl…"
                spellCheck={false}
                className="ms-textarea-dark"
                style={{
                    width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
                    border: `1px solid ${prazdne ? 'rgba(239,68,68,0.45)' : 'rgba(239,68,68,0.25)'}`,
                    resize: 'vertical', minHeight: 56, boxSizing: 'border-box',
                    fontFamily: 'inherit', colorScheme: 'dark',
                    outline: 'none', appearance: 'none',
                    WebkitAppearance: 'none', MozAppearance: 'none',
                }}
            />
        </div>
    );
}
